import { FC } from 'react'

import styled from 'styled-components'
import { useForm, Controller } from 'react-hook-form'
import { notifications } from '@mantine/notifications'

import { Button } from 'src/Components/Button'
import { Input } from 'src/Components/Input/index.tsx'
import { Select } from 'src/Components/Select'
import { Textarea } from 'src/Components/Textarea/index.tsx'
import {
  ToolImage,
  UpdateToolBody,
  useAddToolImagesMutation,
  useDeleteToolImageMutation,
} from 'src/Redux/api/toolsApi.ts'
import { Modal } from 'src/Components/Modal'
import { PhotoGallery } from 'src/Components/PhotoGallery'

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 0;
`

const StyledButton = styled(Button)`
  width: 200px;
  margin: 0 auto;
`

interface EditToolModalProps {
  onClose: () => void
  onUpdateTool: (toolData: UpdateToolBody) => void
  isOpened: boolean
  toolId: number
  images?: ToolImage[]
  initialName: string
  initialPrice: number
  initialCondition?: 'GOOD' | 'SATISFACTORY'
  initialDescription?: string
}

export const EditToolModal: FC<EditToolModalProps> = ({
  isOpened,
  onClose,
  onUpdateTool,
  toolId,
  images = [],
  initialName,
  initialPrice,
  initialCondition,
  initialDescription,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<UpdateToolBody>({
    defaultValues: {
      name: initialName,
      toolWorkPrice: initialPrice,
      condition: initialCondition || 'GOOD',
      description: initialDescription || '',
    },
  })

  const [addToolImages] = useAddToolImagesMutation()
  const [deleteToolImage] = useDeleteToolImageMutation()

  const onSubmit = (data: UpdateToolBody): void => {
    onUpdateTool({
      name: data?.name,
      toolWorkPrice: Number(data?.toolWorkPrice),
      condition: data?.condition,
      description: data?.description,
    })
    reset()
    onClose()
    notifications.show({
      title: 'Навесное оборудование успешно обновлено',
      message: '',
      position: 'top-right',
    })
  }

  return (
    <Modal
      opened={isOpened}
      onClose={onClose}
      title='Редактировать оборудование'
    >
      <ModalContainer>
        <Controller
          name='name'
          control={control}
          render={({ field }) => (
            <Input
              label='Название'
              placeholder='Название'
              error={errors.name?.message}
              {...field}
            />
          )}
        />

        <Controller
          name='toolWorkPrice'
          control={control}
          render={({ field }) => (
            <Input
              type='number'
              label='Цена работ'
              placeholder='Цена работ, руб./ч'
              error={errors.toolWorkPrice?.message}
              {...field}
            />
          )}
        />

        <Controller
          name='condition'
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              label='Состояние'
              placeholder='Выберите состояние'
              data={[
                { value: 'GOOD', label: 'Хорошее' },
                { value: 'SATISFACTORY', label: 'Удовлетворительное' },
              ]}
              error={errors.condition?.message}
            />
          )}
        />

        <Controller
          name='description'
          control={control}
          render={({ field }) => (
            <Textarea
              label='Описание'
              placeholder='Описание'
              minRows={3}
              autosize
              {...field}
            />
          )}
        />

        <PhotoGallery
          title='Фото навесного для КП'
          addLabel='Добавить фото навесного'
          images={images}
          onAdd={(files) => addToolImages({ toolId, images: files })}
          onDelete={(imageId) => deleteToolImage({ toolId, imageId })}
        />

        <StyledButton onClick={handleSubmit(onSubmit)}>Сохранить</StyledButton>
      </ModalContainer>
    </Modal>
  )
}
